// hooks/usePaymentOperations.js
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
    updateContractFinancials as onUpdateContractFinancials,
} from '../../slices/contracts/thunk';
import {
    createInvoice as onCreateInvoice,
    linkInvoice as onLinkInvoice
} from '../../slices/smartbill/thunk'


export const usePaymentOperations = () => {
  const dispatch = useDispatch();
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Payment calculations
  const calculateRemainingBalance = (payments) => {
    return Math.max(
      payments
        .filter(payment => !payment.status?.paid)
        .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0),
      0
    );
  };

  const getCalculatedSettlementPrice = (contract, payments) => {
    return (contract?.total_contract_value && parseFloat(contract.total_contract_value)) ||
           Math.max(...payments.map(p => parseFloat(p.amount || 0)), 0);
  };

  // Payment status logic
  const getPaymentStatusColor = (paymentDate, isPaid) => {
    const today = moment();
    const paymentMoment = moment(paymentDate);
    const diffInDays = paymentMoment.diff(today, "days");

    if (isPaid) return "green";
    if (diffInDays < 0) return "red";
    if (diffInDays <= 7) return "orange";
    return "green";
  };

  const getPaymentStatusText = (paymentDate, isPaid) => {
    if (isPaid) return "Paid";
    if (!paymentDate) return "No due date set";
    
    const statusColor = getPaymentStatusColor(paymentDate, isPaid);
    const statusMap = {
      red: "Overdue",
      orange: "Soon to be paid",
      green: "Upcoming"
    };
    
    return statusMap[statusColor];
  };

  // Utility functions
  const sortPaymentsByDate = (payments) => {
    return [...payments].sort((a, b) => {
      if (!a.booking_date && !b.booking_date) return 0;
      if (!a.booking_date) return 1;
      if (!b.booking_date) return -1;
      return moment(a.booking_date).diff(moment(b.booking_date));
    });
  };

  const hasInvoicedPayments = (payments) => {
    return payments.some(payment => payment.invoice);
  };

  // API operations
  const createInvoiceForPayment = async (requestData) => {
    setInvoiceLoading(true);
    try {
      return await dispatch(onCreateInvoice(requestData)).unwrap();
    } catch (error) {
      console.error('Error creating invoice:', error);

      let errorMessage = 'Failed to create invoice';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data) {
        errorMessage = error.response.data.message || error.response.data.error || `API Error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error - please check your connection';
      }

      toast.error(errorMessage, { autoClose: 3000 });
      return null;
    } finally {
      setInvoiceLoading(false);
    }
  };

  const validatePaymentAmounts = (updatedPayments, totalSettledPrice) => {
    const totalAmount = updatedPayments.reduce(
      (sum, payment) => sum + parseFloat(payment.amount || 0), 0
    );

    if (Math.abs(totalAmount - totalSettledPrice) > 0.01) {
      return `Total payments (€${totalAmount.toFixed(2)}) must match settlement price total (€${totalSettledPrice.toFixed(2)})`;
    }
    return null;
  };

  const getDeletedPaymentIds = (originalPayments, updatedPayments) => {
    const existingPaymentIds = originalPayments
      .filter(p => p.id && !p.id.toString().startsWith("temp-"))
      .map(p => p.id);

    const updatedPaymentIds = updatedPayments
      .filter(p => p.id && !p.id.toString().startsWith("temp-"))
      .map(p => p.id);

    return existingPaymentIds.filter(id => !updatedPaymentIds.includes(id));
  };

  // updatedUnitPrices: [{ unitId, settled_price }, ...] - the contract's units with their
  // (possibly edited) settlement prices. Settlement prices and payments are saved together
  // in one call because they share an invariant: total payments must equal total settled price.
  // Returns { success: true } or { success: false, message } so the caller can surface the
  // error inline (e.g. inside the modal) instead of only via a toast.
  const savePayments = async (updatedPayments, updatedUnitPrices, originalPayments, contract) => {
    try {
      const totalSettledPrice = updatedUnitPrices.reduce(
        (sum, unit) => sum + (parseFloat(unit.settled_price) || 0) + (parseFloat(unit.extra) || 0), 0
      );

      const amountError = validatePaymentAmounts(updatedPayments, totalSettledPrice);
      if (amountError) {
        return { success: false, message: amountError };
      }

      const deletedPaymentIds = getDeletedPaymentIds(originalPayments, updatedPayments);

      const formattedPayments = updatedPayments.map(payment => {
        const isNewPayment = !payment.id || payment.id.toString().startsWith("temp-");
        const amount = parseFloat(payment.amount || 0);
        return {
          ...(isNewPayment ? {} : { id: payment.id }),
          type: payment.type,
          amount,
          booking_date: payment.booking_date,
          vat: payment.vat ?? null,
          cashed: payment.isPaid ? amount : null,
          cashed_date: payment.isPaid ? payment.booking_date : null,
        };
      });

      // settled_price is the unit's amount without extras; extra_finishing is
      // stored separately (the unit's total is the two added together).
      const formattedUnits = updatedUnitPrices.map(unit => ({
        unit: unit.unitId,
        settled_price: parseFloat(unit.settled_price || 0) || 0,
        extra_finishing: parseFloat(unit.extra || 0) || 0,
      }));

      await dispatch(onUpdateContractFinancials({
        contractId: contract.id,
        payload: {
          units: formattedUnits,
          payments: formattedPayments,
          deleted_payment_ids: deletedPaymentIds,
        },
      })).unwrap();

      return { success: true };
    } catch (error) {
      console.error("Error updating payments:", error);
      const message = typeof error === "string" ? error : (error?.message || "Failed to update payments");
      return { success: false, message };
    }
  };

  const linkInvoiceToPayment = async (selectedPayment, series_name, series_number) => {
    const result = await dispatch(onLinkInvoice({payment_ids : selectedPayment, series_name, series_number}))
    return result
  }

  return {
    // State
    invoiceLoading,
    
    // Calculations
    calculateRemainingBalance,
    getCalculatedSettlementPrice,
    
    // Status helpers
    getPaymentStatusColor,
    getPaymentStatusText,
    
    // Utilities
    sortPaymentsByDate,
    hasInvoicedPayments,
    
    // API operations
    createInvoiceForPayment,
    savePayments,
    linkInvoiceToPayment
  };
};